<template>
  <div
    class="avatar"
    :class="stateClass"
    :title="tooltip"
    :style="Object.assign({ width: size + 'px', '--bob': bob + 'px' }, avatarVars)"
  >
    <!-- Glow / aura (uses level-driven vars) -->
    <div class="aura"></div>

    <!-- Chick + egg -->
    <svg
      viewBox="0 0 160 190"
      class="face"
      role="img"
      aria-label="Motivational AI avatar — hatching chick"
    >
      <defs>
        <linearGradient id="shellGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#fff6e5"/>
          <stop offset="1" stop-color="#fbeedb"/>
        </linearGradient>
      </defs>

      <!-- group that bobs up/down -->
      <g class="chick" :class="mood" :style="{ '--open': mouthOpen }">
        <!-- Crown appears on higher levels -->
        <!-- <g v-if="level >= 5" class="crown" :class="{ pop: celebrate || fx.goal }">
          <path d="M55 32 L64 18 L80 32 L95 18 L105 32 L105 40 L55 40 Z"/>
        </g> -->

        <!-- Body -->
        <circle cx="80" cy="72" r="46" class="chick-body"/>
        <!-- Head tuft -->
        <path class="tuft" d="M74 30 q6 -10 12 0 q-4 -6 -2 -12 q-8 8 -10 12" />

        <!-- Wings -->
        <ellipse class="wing" cx="54" cy="98" rx="12" ry="10"/>
        <ellipse class="wing" cx="106" cy="98" rx="12" ry="10"/>

        <!-- Eyes -->
        <g class="eyes" :class="{ blink: blinking }">
          <ellipse cx="60" cy="70" rx="5.8" ry="6.6" class="eye"/>
          <ellipse cx="100" cy="70" rx="5.8" ry="6.6" class="eye"/>
        </g>

        <!-- Cheeks -->
        <circle cx="52" cy="82" r="7" class="cheek"/>
        <circle cx="108" cy="82" r="7" class="cheek"/>

        <!-- Beak -->
        <g class="beak">
          <path class="beak-top" d="M72 84 L88 84 Q80 78 72 84 Z"/>
          <path class="beak-bot" d="M72 84 L88 84 Q80 90 72 84 Z"/>
        </g>
      </g>

      <!-- Egg bottom with crack (wobbles on FX) -->
      <g class="egg">
        <path class="shell"
          d="M24 120
             A56 56 0 0 0 136 120
             L120 98
             L104 115
             L80 100
             L64 115
             L44 98 Z"
          fill="url(#shellGrad)"/>
        <circle cx="80" cy="120" r="56" class="shell-outline" fill="none"/>
      </g>

      <!-- Celebrate burst -->
      <g v-if="celebrate || fx.goal" class="burst">
        <circle
          v-for="i in 14"
          :key="'g'+i"
          :cx="80"
          :cy="100"
          :r="2"
          :transform="`rotate(${(i-1)*(360/14)} 80 100) translate(0 -64)`"
        />
      </g>
    </svg>

    <!-- FX: Step sparks -->
    <div v-if="fx.step" class="fx fx-step">
      <span
        v-for="(sp, i) in fx.stepSparks"
        :key="'s'+i"
        class="spark"
        :style="{ '--rot': sp.rot+'deg', '--tx': sp.dist+'px' }"
      />
      <div class="ring ring-step"></div>
    </div>

    <!-- FX: Task pulse -->
    <div v-if="fx.task" class="fx fx-task">
      <div class="ring ring-task"></div>
      <div class="ring ring-task ring-delay"></div>
    </div>

    <!-- FX: Goal mini-confetti -->
    <div v-if="fx.goal" class="fx fx-goal">
      <span
        v-for="(c, i) in fx.goalConfetti"
        :key="'c'+i"
        class="conf"
        :style="{ '--r': c.rot+'deg', '--tx': c.tx+'px', '--ty': c.ty+'px', '--delay': (i*10)+'ms' }"
      />
    </div>

    <!-- Floating XP chip -->
    <div v-if="fx.xp" class="xp-float">+{{ fx.xp }} XP</div>

    <!-- Trophy pop (when awarded) -->
    <div v-if="trophyPop.show" class="trophy-float">{{ trophyPop.icon }} {{ trophyPop.label }}</div>

    <!-- Trophy shelf (compact, last 5) -->
    <div v-if="trophies.length" class="trophy-shelf" :title="trophiesTitle">
      <span v-for="(tr, i) in trophies.slice(-5)" :key="'tr'+i" class="trophy">{{ tr.icon }}</span>
    </div>

    <!-- Small status caption -->
    <div class="caption" v-if="caption" aria-live="polite">{{ caption }}</div>
  </div>
</template>

<script>
export default {
  name: 'MotivationAvatar',
  props: {
    /* Core reactive state from parent */
    recognizing: { type: Boolean, default: false },
    speaking:    { type: Boolean, default: false },
    thinking:    { type: Boolean, default: false },
    celebrate:   { type: Boolean, default: false },
    mood:        { type: String,  default: 'friendly' }, // friendly | upbeat | focused | proud
    energy:      { type: Number,  default: 0 },          // 0..1, drives mouth/bob when not speaking
    caption:     { type: String,  default: '' },
    size:        { type: Number,  default: 220 },
    level:       { type: Number,  default: 1 },

    /* Extra reactivity hooks (from Code 9) */
    question:    { type: String,  default: '' },         // latest user utterance (text)
    answer:      { type: String,  default: '' },         // latest assistant answer (text)
    command:     { type: Object,  default: null },       // one-shot control from parent
    xp:          { type: Number,  default: 0 },          // direct XP pulse
  },
  data() {
    return {
      mouthLevel: 0,
      blinking: false,
      blinkTimer: null,
      mouthRAF: 0,

      // FX manager
      fx: { step: false, stepSparks: [], task: false, goal: false, goalConfetti: [], xp: 0 },
      fxTimers: [],

      // trophies (visual only here)
      trophies: [],
      trophyPop: { show: false, icon: '🏆', label: '' },

      // cached theme object derived from level
      theme: this.levelToTheme(1),

      // internal guards for duplicate questions/commands (avoid _ reserved keys)
      lastQToken: '',
      lastCmdIdToken: 0,
    };
  },
  computed: {
    stateClass() {
      return {
        'is-listening': this.recognizing && !this.speaking,
        'is-speaking':  this.speaking,
        'is-thinking':  this.thinking,
        'is-celebrate': this.celebrate || this.fx.goal,
        'is-fx':        this.fx.step || this.fx.task || this.fx.goal,
      };
    },
    tooltip() {
      if (this.celebrate || this.fx.goal) return 'Celebrating a win!';
      if (this.speaking) return 'Speaking…';
      if (this.recognizing) return 'Listening…';
      if (this.thinking) return 'Thinking…';
      return 'Ready';
    },
    mouthOpen() {
      const e = Math.max(0, Math.min(1, this.energy || 0));
      // Speak mouth = wide; otherwise idle follows energy (voice viz)
      return this.speaking ? 0.35 + this.mouthLevel * 0.6 : 0.08 + e * 0.15;
    },
    bob() {
      const e = Math.max(0, Math.min(1, this.energy || 0));
      return (this.speaking ? 2.2 : 0.8) + e * 3.2;
    },
    // expose level-driven CSS vars
    avatarVars() {
      const t = this.theme;
      return {
        '--accentA': t.a,
        '--accentB': t.b,
        '--accentAsoft': t.aSoft,
        '--ringColor': t.ring,
        '--burstColor': t.burst
      };
    },
    trophiesTitle() {
      const counts = this.trophies.reduce((m, t) => (m[t.kind]=(m[t.kind]||0)+1, m), {});
      const parts = Object.keys(counts).map(k => `${k}: ${counts[k]}`);
      return `Trophies — ${parts.join(', ')}`;
    }
  },
  watch: {
    /* Movement / feel */
    speaking(n) { if (n) this.pulseMouth(); },
    celebrate(n) { if (n) this.celebrateGoal(0); },
    caption() { this.bumpMouth(0.25); },

    // Theme reacts to level and informs parent (for page theming)
    level: {
      immediate: true,
      handler(n){
        this.theme = this.levelToTheme(n);
        this.$emit('theme', { level: n, theme: this.theme });
      }
    },

    /* ==== Parent → Avatar reactivity (from Code 9) ==== */
    // Briefly animate mouth when an answer arrives (parent still does real TTS)
    answer(txt) {
      if ((txt || '').trim()) this.bumpMouth(0.6);
    },
    // Float XP when parent sets it
    xp(n) {
      const v = Number(n || 0);
      if (v > 0) this.flashXP(v);
    },
    // Parse questions into intents to drive parent flows
    question(q) {
      const s = (q || '').trim();
      if (!s || s === this.lastQToken) return;
      this.lastQToken = s;
      const intent = this.parseIntent(s);
      if (intent) {
        this.$emit('intent', intent); // parent handles form opening & saving
        this.bumpMouth(0.4);
      }
    },
    // Execute one-shot command from parent
    command(cmd) {
      if (!cmd || typeof cmd !== 'object') return;
      const id = (cmd.id != null ? cmd.id : cmd.__id) ?? Date.now(); // support both id and __id
      if (id === this.lastCmdIdToken) return;
      this.lastCmdIdToken = id;
      try {
        const { type, payload = {} } = cmd;
        if (type === 'celebrate-step')  this.celebrateStep(payload.xp || 25,  payload.msg || 'Step complete!');
        if (type === 'celebrate-task')  this.celebrateTask(payload.xp || 15,  payload.msg || 'Task completed!');
        if (type === 'celebrate-goal')  this.celebrateGoal(payload.xp || 200, payload.msg || 'Goal completed!');
        if (type === 'xp')              this.flashXP(payload.xp || 10);
        if (type === 'caption')         this._say(payload.text || '');
        if (type === 'react')           this.react(payload.kind, payload);
        this.$emit('ack', { id, ok: true, type });
      } catch (e) {
        this.$emit('ack', { id, ok: false, error: String((e && e.message) || e) });
      }
    },
  },
  mounted() {
    this.scheduleBlink();
    this.$emit('theme', { level: this.level, theme: this.theme });
    this.$emit('ready');
  },
  beforeDestroy() {
    cancelAnimationFrame(this.mouthRAF);
    if (this.blinkTimer) clearTimeout(this.blinkTimer);
    this.fxTimers.forEach(id => clearTimeout(id));
    this.fxTimers = [];
  },
  methods: {
    /* ===== public FX API ===== */
    celebrateStep(xp = 25, msg = 'Step complete!') {
      this._burstStep(xp);
      this._say(msg);
      this.awardTrophy('step');
      this.$emit('fx', { kind: 'step', xp, msg });
      this.$emit('trophy', { kind: 'step' });
    },
    celebrateTask(xp = 15, msg = 'Task completed!') {
      this._burstTask(xp);
      this._say(msg);
      this.awardTrophy('task');
      this.$emit('fx', { kind: 'task', xp, msg });
      this.$emit('trophy', { kind: 'task' });
    },
    celebrateGoal(xp = 200, msg = 'Goal completed!') {
      this._burstGoal(xp);
      if (msg) this._say(msg);
      this.awardTrophy('goal');
      this.$emit('fx', { kind: 'goal', xp, msg });
      this.$emit('trophy', { kind: 'goal' });
    },
    react(kind, { xp = 0, msg = '' } = {}) {
      if (kind === 'step')  return this.celebrateStep(xp || 25, msg || 'Step complete!');
      if (kind === 'task')  return this.celebrateTask(xp || 15, msg || 'Task completed!');
      if (kind === 'goal')  return this.celebrateGoal(xp || 200, msg || 'Goal completed!');
    },
    flashXP(n=10){ this.fx.xp = n; setTimeout(()=> (this.fx.xp = 0), 1100); },
    showAnswer(text=''){ this._say(text); },

    /* ===== mouth / idle motion ===== */
    pulseMouth() {
      cancelAnimationFrame(this.mouthRAF);
      const tick = () => {
        this.mouthLevel = Math.max(0, this.mouthLevel * 0.88 - 0.008);
        this.mouthRAF = requestAnimationFrame(tick);
      };
      this.mouthRAF = requestAnimationFrame(tick);
    },
    bumpMouth(intensity = 1) {
      this.mouthLevel = Math.min(1, this.mouthLevel + 0.35 * intensity);
    },
    scheduleBlink() {
      const next = 1800 + Math.random() * 2400;
      this.blinkTimer = setTimeout(() => {
        this.blinking = true;
        setTimeout(() => { this.blinking = false; this.scheduleBlink(); }, 140);
      }, next);
    },

    /* ===== FX builders ===== */
    _burstStep(xp) {
      const N = 10;
      this.fx.stepSparks = Array.from({ length: N }, () => ({
        rot: Math.random() * 360,
        dist: 30 + Math.random() * 32
      }));
      this.fx.xp = xp;
      this.fx.step = true;
      this.bumpMouth(1.2);
      this.fxTimers.push(setTimeout(() => { this.fx.step = false; }, 820));
      this.fxTimers.push(setTimeout(() => { this.fx.xp = 0; }, 1000));
    },
    _burstTask(xp) {
      this.fx.xp = xp;
      this.fx.task = true;
      this.bumpMouth(1.0);
      this.fxTimers.push(setTimeout(() => { this.fx.task = false; }, 900));
      this.fxTimers.push(setTimeout(() => { this.fx.xp = 0; }, 1100));
    },
    _burstGoal(xp) {
      const N = 18;
      this.fx.goalConfetti = Array.from({ length: N }, () => ({
        rot: Math.random() * 360,
        tx: -10 + Math.random() * 20,
        ty: -10 + Math.random() * 20
      }));
      this.fx.xp = xp || this.fx.xp;
      this.fx.goal = true;
      this.bumpMouth(1.4);
      this.fxTimers.push(setTimeout(() => { this.fx.goal = false; }, 1200));
      this.fxTimers.push(setTimeout(() => { this.fx.xp = 0; }, 1400));
    },

    _say(msg) { if (msg) this.bumpMouth(0.6); },

    /* ===== trophies + theming ===== */
    awardTrophy(kind) {
      const map = { step: { icon: '⭐', label: 'Step' }, task: { icon: '🎖️', label: 'Task' }, goal: { icon: '🏆', label: 'Goal' } };
      const td = map[kind] || map.step;
      this.trophies.push({ kind, icon: td.icon, ts: Date.now() });
      this.trophyPop = { show: true, icon: td.icon, label: td.label };
      setTimeout(() => { this.trophyPop.show = false; }, 1100);
    },

    levelToTheme(level = 1) {
      // advance palette every 2 levels
      const idx = Math.min(4, Math.floor((Math.max(1, level) - 1) / 2));
      const palettes = [
        { a:'#ffc24b', b:'#28d07f' }, // 1–2 hatchling
        { a:'#8d79ff', b:'#00f5c8' }, // 3–4 sprout
        { a:'#ff6e6e', b:'#ffc24b' }, // 5–6 soar
        { a:'#4cc9f0', b:'#90f1ff' }, // 7–8 sky
        { a:'#ff9f1c', b:'#2ec4b6' }, // 9+ legend
      ];
      const p = palettes[idx];
      const aSoft = this.hex2rgba(p.a, 0.26);
      return { a: p.a, b: p.b, aSoft, ring: this.hex2rgba(p.b, 0.55), burst: p.a };
    },
    hex2rgba(hex, a=1){
      const h = hex.replace('#','');
      const bigint = parseInt(h.length===3 ? h.split('').map(x=>x+x).join('') : h, 16);
      const r = (bigint>>16)&255, g=(bigint>>8)&255, b=bigint&255;
      return `rgba(${r},${g},${b},${a})`;
    },

    /* ===== Intent parser (enhanced) ===== */
    parseIntent(raw='') {
      const s = raw.trim();
      const low = s.toLowerCase();

      const strip = (text) => text.replace(/\s+/g,' ').trim();
      const extractAfter = (re, txt) => {
        const m = txt.match(re);
        return m ? strip(txt.slice(m.index + m[0].length)) : '';
      };
      const duePhraseOf = (txt) => {
        const m = txt.match(/\b(?:by|due|on)\s+([^,.;]+)(?=[,.;]|$)/i);
        return m ? strip(m[1]) : '';
      };
      const stripDue = (txt) => txt.replace(/\b(?:by|due|on)\s+[^,.;]+/i, '').trim();
      const stripLeadingTo = (txt) => txt.replace(/^to\s+/i, '').trim();
      const priorityOf = (txt) => {
        const m = txt.match(/\bpriority\s*[:]?\s*(low|medium|high)\b/i);
        return m ? (m[1][0].toUpperCase() + m[1].slice(1).toLowerCase()) : '';
      };
      const categoryOf = (txt) => {
        const m = txt.match(/\bcategory\s*[:]?\s*([A-Za-z]+)\b/);
        return m ? m[1] : '';
      };
      const outcomeOf = (txt) => {
        const m = txt.match(/\b(?:because|so that)\s+(.+)$/i);
        return m ? strip(m[1]) : '';
      };

      // create task
      if (/(^|\s)(add|start|create)\s+task\b/i.test(low)) {
        let title = extractAfter(/(^|\s)(add|start|create)\s+task\b/i, s);
        const duePhrase = duePhraseOf(title); title = stripLeadingTo(stripDue(title)).replace(/^[,.:;-]\s*/,'');
        return { type:'create-task', title: title || '', duePhrase, raw: s };
      }

      // create goal
      if (/^(start|create|add)\s+(?:a\s+)?(?:new\s+)?goal\b/i.test(s)) {
        let body = extractAfter(/^(start|create|add)\s+(?:a\s+)?(?:new\s+)?goal\b/i, s);
        const duePhrase = duePhraseOf(body);
        const priority = priorityOf(body);
        const category = categoryOf(body);
        const outcome = outcomeOf(body);
        const title = stripLeadingTo(stripDue(body)).replace(/^[,.:;-]\s*/,'');
        return { type:'create-goal', title, duePhrase, priority, category, outcome, raw: s };
      }
      // shorthand "goal: ..."
      const colonGoal = s.match(/^goal\s*(?::|-)\s*(.+)$/i);
      if (colonGoal) {
        const body = colonGoal[1];
        return {
          type:'create-goal',
          title: stripLeadingTo(stripDue(body)),
          duePhrase: duePhraseOf(body),
          priority: priorityOf(body),
          category: categoryOf(body),
          outcome: outcomeOf(body),
          raw: s
        };
      }

      // complete goal / task
      if (/^complete\s+goal\b/i.test(low) || /^(finish|done)\s+goal\b/i.test(low)) {
        return { type:'complete-goal', raw: s };
      }
      const completeTask = s.match(/^complete\s+task\s+(.+)$/i) || s.match(/^(finish|done)\s+task\s+(.+)$/i);
      if (completeTask) {
        const title = strip(s.replace(/^complete\s+task\s+/i,'').replace(/^(finish|done)\s+task\s+/i,''));
        return { type:'complete-task', title, raw: s };
      }

      // open intake/profile
      if (/^(open|show|edit|setup|set up)\s+(intake|profile|coach profile)\b/i.test(low)) {
        return { type:'open-intake', raw: s };
      }

      // status / next step
      if (/^(status|what'?s next|what is next)\b/i.test(low)) {
        return { type:'status', raw: s };
      }

      // save/cancel
      if (/^(ok(ay)?\s+)?save\b|^(done|finish(ed)?|looks good|good to go)$/i.test(low)) {
        return { type:'save', raw: s };
      }
      if (/^(cancel|close|dismiss|never ?mind|stop)$/i.test(low)) {
        return { type:'cancel', raw: s };
      }

      return null;
    },
  }
};
</script>

<style scoped>
/* ========= container ========= */
.avatar{
  position: relative;
  user-select: none;
  transition: transform .18s ease;
  margin-left: 25px;
  margin-top: 15px;
  padding: 12px 12px 12px 12px;
}
.avatar.is-fx { transform: translateY(-1px) scale(1.01); }

/* aura — uses CSS vars from level theme */
.aura{
  position:absolute; inset:-12px; border-radius:24px;
  background: radial-gradient(40% 40% at 50% 50%, var(--accentAsoft), rgba(0,0,0,0));
  filter: blur(12px); opacity:.85;
  transition: transform .25s ease, opacity .25s ease, box-shadow .25s ease;
}
.avatar.is-listening .aura { box-shadow: 0 0 0 2px var(--ringColor) inset; }
.avatar.is-speaking  .aura { box-shadow: 0 0 0 2px var(--ringColor) inset; }
.avatar.is-thinking  .aura { animation: throb 1.6s ease-in-out infinite; }
.avatar.is-fx .aura      { opacity:.95; box-shadow: 0 0 0 3px var(--ringColor) inset; }
@keyframes throb { 0%{transform:scale(.985)} 50%{transform:scale(1.015)} 100%{transform:scale(.985)} }

/* ========= SVG ========= */
.face{
  display:block; width:100%; height:200px;
  filter: drop-shadow(0 8px 24px rgba(0,0,0,.35));
}

/* Chick group bobbing */
.chick { transform: translateY(calc(-1 * var(--bob))); transition: transform .25s ease; }

/* Lines & fills */
:root, .avatar{
  --line: #6b4b2b;         /* brown outline */
  --yolk: #ffd96a;         /* chick yellow */
  --cheek: #ffc8a3;        /* cheek pink */
  --shell-line: #6b4b2b;
}
.chick-body{ fill: var(--yolk); stroke: var(--line); stroke-width: 5; }
.wing{ fill: var(--yolk); stroke: var(--line); stroke-width: 5; }
.tuft{ fill:none; stroke: var(--line); stroke-width:5; stroke-linecap:round; }

.eye{ fill: var(--line); }
.eyes.blink .eye{ transform-origin: center; transform: scaleY(0.1); transition: transform .12s ease; }

.cheek{ fill: var(--cheek); opacity:.22; transition: transform .25s ease; }
.avatar.is-celebrate .cheek{ animation: cheekPulse .9s ease-out; }
@keyframes cheekPulse{ 0%{transform:scale(.95)} 50%{transform:scale(1.1)} 100%{transform:scale(1)} }

/* Beak */

/* Crown for higher levels */
.crown path{
  fill: var(--accentA);
  stroke: var(--line); stroke-width: 4; stroke-linejoin:round;
  filter: drop-shadow(0 3px 6px rgba(0,0,0,.25));
}
.crown{ transform: translate(0,-8px); transform-origin: 80px 26px; }
.crown.pop{ animation: crownPop .6s ease-out; }
@keyframes crownPop{ 0%{transform:translate(0,-14px) scale(.8)} 50%{transform:translate(0,-8px) scale(1.06)} 100%{transform:translate(0,-8px) scale(1)} }

/* Egg */
.shell{ stroke: var(--shell-line); stroke-width: 5; fill: url(#shellGrad); }
.shell-outline{ stroke: var(--shell-line); stroke-width: 0; }

/* wobble on fx / celebrate */
.avatar.is-fx .egg,
.avatar.is-celebrate .egg{ animation: wobble .6s ease-out; }
@keyframes wobble{
  0% { transform: rotate(0deg) }
  20%{ transform: rotate(-6deg) }
  40%{ transform: rotate(4deg) }
  60%{ transform: rotate(-2deg) }
  100% { transform: rotate(0deg) }
}

/* ===== Celebrate burst ===== */
.burst circle { fill: var(--burstColor); transform-origin: 80px 100px; animation: pop .6s ease-out forwards; }
@keyframes pop { from { transform: scale(.2); opacity:0 } to { transform: scale(1); opacity:1 } }

/* ===== FX layers ===== */
.fx { position:absolute; inset:0; pointer-events:none; }

/* Step shards + ring */
.fx-step .spark{
  position:absolute; left:50%; top:50%;
  width:8px; height:8px; border-radius:2px;
  background: linear-gradient(135deg, var(--burstColor), var(--accentB));
  transform: translate(-50%, -50%) rotate(var(--rot)) translateX(var(--tx)) scale(.6);
  animation: sparkOut .7s ease-out forwards;
  box-shadow: 0 0 10px rgba(0,0,0,.25);
}
@keyframes sparkOut {
  0%   { opacity: 0; transform: translate(-50%,-50%) rotate(var(--rot)) translateX(0) scale(.3); }
  20%  { opacity: 1; }
  100% { opacity: 0; transform: translate(-50%,-50%) rotate(var(--rot)) translateX(var(--tx)) scale(.9); }
}

.ring{
  position:absolute; left:50%; top:50%;
  width:120px; height:120px; border-radius:50%;
  transform: translate(-50%, -50%) scale(.7);
  border:2px solid var(--ringColor);
  box-shadow: 0 0 20px rgba(0,0,0,.25), inset 0 0 12px rgba(255,255,255,.15);
  animation: ringPulse .7s ease-out forwards;
}
.ring-step { }
@keyframes ringPulse {
  0%   { opacity: 0; transform: translate(-50%,-50%) scale(.6); }
  30%  { opacity: 1; }
  100% { opacity: 0; transform: translate(-50%,-50%) scale(1.15); }
}
.fx-task .ring-task { border-color: var(--ringColor); }
.fx-task .ring-delay { animation-delay: .15s; }

.fx-goal .conf{
  position:absolute; left:50%; top:50%;
  width:8px; height:12px; border-radius:2px;
  transform: translate(-50%,-50%) rotate(var(--r));
  background: currentColor;
  color: var(--burstColor);
  animation: confPop .9s ease-out forwards;
  animation-delay: var(--delay);
}
.fx-goal .conf:nth-child(4n+1){ color: var(--burstColor); }
.fx-goal .conf:nth-child(4n+2){ color: var(--accentB); }
.fx-goal .conf:nth-child(4n+3){ color:#ff6e6e; }
.fx-goal .conf:nth-child(4n+4){ color:#8d79ff; }
@keyframes confPop {
  0%   { opacity:0; transform: translate(-50%,-50%) rotate(var(--r)) translate(0,0) scale(.6); }
  20%  { opacity:1; }
  100% { opacity:0; transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) rotate(var(--r)) scale(1); }
}

/* Floating XP chip */
.xp-float{
  position:absolute; left:50%; top:8%;
  transform: translateX(-50%) translateY(10px);
  padding: 4px 10px; border-radius: 999px;
  font-size: 12px; font-weight: 800; letter-spacing:.3px;
  color: #402c19; background: linear-gradient(90deg,var(--accentB),var(--burstColor));
  box-shadow: 0 8px 20px rgba(0,0,0,.35);
  animation: xpRise .9s ease-out forwards;
}
@keyframes xpRise {
  0%   { opacity:0; transform: translateX(-50%) translateY(12px) scale(.92); }
  20%  { opacity:1; }
  100% { opacity:0; transform: translateX(-50%) translateY(-18px) scale(1.02); }
}

/* Trophy FX + Shelf */
.trophy-float{
  position:absolute; left:50%; top:-6px;
  transform: translateX(-50%) translateY(10px);
  padding: 4px 10px; border-radius: 999px;
  font-size: 12px; font-weight: 800;
  background: rgba(0,0,0,.55); color: #fff;
  box-shadow: 0 8px 20px rgba(0,0,0,.35);
  animation: xpRise .95s ease-out forwards;
}
.trophy-shelf{
  position:absolute; right:50px; bottom:-46px;
  margin-bottom: 15px;
  display:flex; gap:4px; padding:4px 6px; border-radius:12px;
  background: rgba(255,255,255,0.08); border:1px solid rgba(255,255,255,0.16);
  font-size: 14px; filter: drop-shadow(0 4px 12px rgba(0,0,0,.25));
}
.trophy{ display:inline-block; transform: translateY(0); }

/* Caption */
.caption { text-align:center; margin-top:6px; font-size:12px; color:#a9afd2; }

/* Mood flourishes */
.chick.upbeat .tuft{ transform: translateY(-1px) rotate(-6deg); transform-origin: 78px 30px; }
.chick.focused .eyes .eye{ rx:5.2; ry:5.6; }
.chick.proud .cheek{ transform: translateY(-1px) scale(1.08); }

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .aura, .eyes .eye, .burst circle, .spark, .ring, .conf, .xp-float, .egg, .trophy-float, .crown{
    animation: none !important;
    transition: none !important;
  }
}
</style>
